import React, { useState, useEffect, useRef } from 'react';
import { Terminal } from 'lucide-react';
import { passwordManagerAPI } from '../../services/api';
import { useSearch } from '../../contexts/SearchContext';
import { useDarkMode } from '../../contexts/DarkModeContext';
import loggingService from '../../services/loggingService';

interface ActivityLog {
	id: number;
	user_id: number;
	action: string;
	description: string;
	ip_address?: string;
	platform?: string;
	metadata?: any;
	log_api_type?: string | null;
	created_at: string;
}

const Logs: React.FC = () => {
	const { globalSearchTerm } = useSearch();
	const { isDark } = useDarkMode();
	const [logs, setLogs] = useState<ActivityLog[]>([]);
	const [loading, setLoading] = useState(true);
	const hasLoggedPageView = useRef(false);

	useEffect(() => {
		loadData();
		if (!hasLoggedPageView.current) {
			loggingService.logPageView('/password-manager/logs', 'API Key Manager', 'Activity Logs');
			hasLoggedPageView.current = true;
		}
	}, []);

	const loadData = async () => {
		try {
			const response = await passwordManagerAPI.logs.getAll({ limit: 100 });
			setLogs(response.data.data);
		} catch (error) {
			console.error('Error loading logs:', error);
		} finally {
			setLoading(false);
		}
	};

	const filteredLogs = logs.filter(log => {
		const matchesSearch = globalSearchTerm === '' ||
			log.action.toLowerCase().includes(globalSearchTerm.toLowerCase()) ||
			log.description.toLowerCase().includes(globalSearchTerm.toLowerCase());
		return matchesSearch;
	});

	if (loading) {
		return (
			<div className="flex flex-col items-center justify-center h-64 font-mono">
				<div className="w-10 h-10 border-2 border-t-transparent rounded-none animate-spin border-emerald-500 mb-4" />
				<span className="text-[10px] uppercase tracking-[0.2em] text-emerald-500/70 animate-pulse">
					[ACCESSING_SYSTEM_JOURNAL...]
				</span>
			</div>
		);
	}

	return (
		<div className="space-y-6">
			<div className="flex items-center justify-between">
				<div>
					<h1 className={`text-xl font-mono font-bold uppercase tracking-[0.3em] ${isDark ? 'text-emerald-400' : 'text-emerald-700'}`}>
						[SYS.ACTIVITY_LOGS]
					</h1>
					<div className="flex items-center space-x-2 mt-1">
						<div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
						<p className={`text-[10px] font-mono uppercase tracking-widest ${isDark ? 'text-neutral-500' : 'text-neutral-400'}`}>
							LIVE_FEED_ACTIVE
						</p>
					</div>
				</div>
			</div>

			<div className={`border rounded-none overflow-hidden ${isDark ? 'border-neutral-800 bg-[#0a0a0a]' : 'border-neutral-200 bg-white'} shadow-[0_10px_30px_rgba(0,0,0,0.1)]`}>
				<div className={`flex items-center px-4 py-2 border-b ${isDark ? 'bg-[#111] border-neutral-800' : 'bg-neutral-50 border-neutral-200'}`}>
					<div className="flex space-x-1.5 mr-4">
						<div className="w-2 h-2 rounded-full bg-[#ff5f56]" />
						<div className="w-2 h-2 rounded-full bg-[#ffbd2e]" />
						<div className="w-2 h-2 rounded-full bg-[#27c93f]" />
					</div>
					<span className={`text-[9px] font-mono font-bold uppercase tracking-[0.2em] ${isDark ? 'text-neutral-600' : 'text-neutral-400'}`}>
						TERMINAL_BUFFER_V2.0.4
					</span>
				</div>
				<div className="divide-y divide-neutral-900/50 max-h-[70vh] overflow-y-auto font-mono">
					{filteredLogs.map((log) => (
						<div key={log.id} className={`p-4 group transition-colors ${isDark ? 'hover:bg-emerald-500/[0.03]' : 'hover:bg-emerald-500/[0.02]'}`}>
							<div className="flex items-start space-x-4">
								<div className={`mt-1 text-[10px] opacity-40 px-1 border group-hover:opacity-100 transition-opacity ${isDark ? 'border-neutral-800 text-neutral-500 group-hover:border-emerald-500/30 group-hover:text-emerald-500' : 'border-neutral-200 text-neutral-400 group-hover:border-emerald-600/30 group-hover:text-emerald-700'}`}>
									{new Date(log.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false })}
								</div>
								
								<div className="flex-1 min-w-0">
									<div className="flex items-center flex-wrap gap-2 mb-1">
										<span className={`text-[10px] font-bold uppercase tracking-widest ${isDark ? 'text-emerald-400' : 'text-emerald-700'}`}>
											&gt; {log.action}
										</span>
										{log.log_api_type && (
											<span className={`text-[8px] px-1.5 py-0.5 border uppercase tracking-[0.1em] ${isDark ? 'border-emerald-500/30 text-emerald-500/60 bg-emerald-500/5' : 'border-emerald-600/30 text-emerald-700/60 bg-emerald-50'}`}>
												[{log.log_api_type}]
											</span>
										)}
									</div>
									
									<p className={`text-[11px] uppercase tracking-wide leading-relaxed break-words ${isDark ? 'text-neutral-400' : 'text-neutral-600'}`}>
										{log.description}
									</p>
									
									<div className="flex items-center space-x-3 mt-2 opacity-0 group-hover:opacity-100 transition-opacity">
										<span className={`text-[8px] uppercase tracking-tighter ${isDark ? 'text-neutral-700' : 'text-neutral-400'}`}>
											TIMESTAMP: {new Date(log.created_at).toLocaleString('tr-TR')}
										</span>
										{log.ip_address && (
											<span className={`text-[8px] uppercase tracking-tighter ${isDark ? 'text-neutral-700' : 'text-neutral-400'}`}>
												IP: {log.ip_address}
											</span>
										)}
										<span className={`text-[8px] uppercase tracking-tighter ${isDark ? 'text-neutral-700' : 'text-neutral-400'}`}>
											ID: {log.id.toString().padStart(6, '0')}
										</span>
									</div>
								</div>
							</div>
						</div>
					))}
				</div>
			</div>

			{filteredLogs.length === 0 && (
				<div className="text-center py-12">
					<h3 className={`text-lg font-medium mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>No Logs Found</h3>
					<p className={isDark ? 'text-gray-400' : 'text-gray-600'}>No activity logs available.</p>
				</div>
			)}
		</div>
	);
};

export default Logs;

